import React, { useState, useEffect } from 'react';
import { Box, Container, Typography } from '@mui/material';
import ConversationSharePlayer from './ConversationSharePlayer';
import { RightSidePanel } from '@/common/Sidepanel';
import { DocumentViewerContainer } from '@/components/ConverseClone/document';

// Custom hook for managing document head meta tags
const useDocumentMeta = (title: string, description: string, imageUrl?: string) => {
  useEffect(() => {
    // Save original meta tags to restore them when component unmounts
    const originalTitle = document.title;
    const originalMetaTags: HTMLMetaElement[] = Array.from(document.head.querySelectorAll('meta[property^="og:"], meta[name="description"], meta[name^="twitter:"]'));
    const originalMetaValues = originalMetaTags.map(tag => ({ 
      element: tag, 
      property: tag.getAttribute('property') || tag.getAttribute('name'),
      content: tag.getAttribute('content')
    }));
    
    // Update title
    document.title = title;
    
    // Helper function to create or update meta tags
    const setMetaTag = (property: string, content: string, isOg: boolean = true) => {
      const propertyAttr = isOg ? 'property' : 'name';
      const propertyValue = isOg ? `og:${property}` : property;
      
      // Look for existing tag
      let meta = document.querySelector(`meta[${propertyAttr}="${propertyValue}"]`) as HTMLMetaElement;
      
      // Create new tag if it doesn't exist
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(propertyAttr, propertyValue);
        document.head.appendChild(meta);
      }
      
      // Set content
      meta.setAttribute('content', content);
    };
    
    // Set OpenGraph meta tags
    setMetaTag('title', title);
    setMetaTag('description', description);
    setMetaTag('description', description, false);
    setMetaTag('type', 'website');
    setMetaTag('url', window.location.href);
    
    // Set Twitter Card meta tags
    setMetaTag('twitter:card', 'summary_large_image', false);
    setMetaTag('twitter:title', title, false);
    setMetaTag('twitter:description', description, false);
    
    // Set image if available
    if (imageUrl) {
      setMetaTag('image', imageUrl);
      setMetaTag('twitter:image', imageUrl, false);
    }
    
    // Cleanup function to restore original meta tags when component unmounts
    return () => {
      document.title = originalTitle;
      
      // Remove tags we added
      document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]').forEach(tag => {
        const property = tag.getAttribute('property') || tag.getAttribute('name');
        // Only remove if it wasn't in the original set
        if (property && !originalMetaValues.some(original => original.property === property)) {
          tag.remove();
        }
      });
      
      // Restore original values
      originalMetaValues.forEach(({ element, property, content }) => {
        if (property && content) {
          const attr = property.startsWith('og:') ? 'property' : 'name';
          element.setAttribute(attr, property);
          element.setAttribute('content', content);
        }
      });
    };
  }, [title, description, imageUrl]);
};

interface SharePageProps {
  sessionId: string;
}

const SharePage: React.FC<SharePageProps> = ({ sessionId }) => {
  const [documentOpen, setDocumentOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState('Shared Conversation');
  const documentPanelWidth = '40vw';
  
  // Set meta tags for social media previews
  useDocumentMeta(
    conversationTitle, 
    `View this shared AI conversation ${sessionId ? `(ID: ${sessionId})` : ''} powered by Alchemyst AI`,
  );

  useEffect(() => {
    setDocumentOpen(!!documentUrl);
  }, [documentUrl]);

  if (!sessionId) {
    return (
      <Container>
        <Typography variant="h4" sx={{ mt: 4 }}>
          No session ID provided
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4, display: 'flex' }}>
      <Box
        marginRight={documentOpen ? documentPanelWidth : 0}
        maxWidth={documentOpen ? `calc(100% - ${documentPanelWidth})` : '100%'}
        sx={{ transition: 'margin-right 0.3s, max-width 0.3s', width: '100%' }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ mb: 4 }}>
            Shared Conversation : {conversationTitle}
          </Typography>
          <ConversationSharePlayer 
            sessionId={sessionId} 
            onDocumentUrlChange={setDocumentUrl}
            onTitleChange={setConversationTitle}
          />
        </Container>
      </Box>

      <RightSidePanel
        open={documentOpen}
        setOpen={setDocumentOpen}
        panelWidth={documentPanelWidth}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <DocumentViewerContainer />
          </Box>
        </Box>
      </RightSidePanel>
    </Box>
  );
};

export default SharePage;
